import { dehydrate, QueryClient } from '@tanstack/react-query'
import { GetServerSideProps, GetServerSidePropsContext } from 'next'

import { Page } from '../components/Page'
import { Client } from '../customClients/client'
import { HTTPStatus } from '../customClients/httpStatus'
import { checkProtectedRoute } from '../helpers/checkProtectedRoute'
import { authOptions } from '../helpers/nextAuth/config'
import { ServerSideAuthenticator } from '../helpers/serverSideAuthenticator'

export const getServerSideProps: GetServerSideProps = async (
  ctx: GetServerSidePropsContext
) => {
  const { resolvedUrl } = ctx
  ctx.res.setHeader('Cache-Control', 'private, stale-while-revalidate=3')

  const queryClient = new QueryClient()
  const urlParts = resolvedUrl.split('?')
  const urlSlug = urlParts?.[0]

  const searchParams = urlParts?.[1] ? urlParts?.[1] : ''
  const params = searchParams?.split('&')
  let token = params?.find((param) => {
    return param.startsWith('token')
  })
  const preview = params?.find((param) => {
    return param.startsWith('live_preview')
  })
  const authenticator = new ServerSideAuthenticator(ctx, authOptions)

  if (urlSlug === '/recoverypassword' && token) {
    token = token.split('=')?.[1]
    const isAccountMigrationFlow = params?.some((queryParam) => {
      return queryParam.includes('reset-password')
    })

    const resetPassword = isAccountMigrationFlow ? '&reset-password=1' : ''

    return {
      redirect: {
        destination: `/?token=${token}${resetPassword}#reset-password`,
        permanent: false,
      },
      props: {},
    }
  }

  const filteredQueryParams: string[] = []
  const utmQueryParams: string[] = []

  params?.forEach((param) => {
    if (param.startsWith('utm')) {
      utmQueryParams.push(param)
    }

    if (
      !param.startsWith('utm') &&
      !param.startsWith('gclid') &&
      !param.startsWith('sc_') &&
      !param.startsWith('token')
    ) {
      filteredQueryParams.push(param)
    }
  })

  const filteredQueryString = filteredQueryParams.join('&')
  const utmQueryString = encodeURIComponent(JSON.stringify(utmQueryParams))

  const domain = ctx.req.headers['x-forwarded-host'] || ctx.req.headers.host

  const queryString = `slug=${urlSlug}&domain=${domain}&${filteredQueryString}&utmQueryParams=${utmQueryString}`

  const session = await authenticator.getSession()
  queryClient.setQueryData(['session'], session)

  let pageResult = await Client.cms.getPage({
    queryClient,
    queryString,
    session,
  })
  let page

  // Redirect to Home with Redirect URL when user Logs out and is on a Protected Route
  const isProtectedRoute = checkProtectedRoute(urlSlug)

  let isLoggedIn = false

  if (session) {
    isLoggedIn = !session?.token?.anonymousId
  }

  if (isProtectedRoute && !isLoggedIn) {
    return {
      redirect: {
        destination: `/?redirectUrl=${urlSlug}#login`,
        permanent: false,
      },
      props: {},
    }
  }

  if (pageResult.getErrorSafe()) {
    const error = pageResult.getErrorSafe()
    if (error.status === HTTPStatus.Unauthorized) {
      const session = await authenticator.refreshToken()
      pageResult = await Client.cms.getPage({
        queryClient,
        queryString,
        session,
      })
    }
  }

  if (pageResult.getErrorSafe()) {
    const error = pageResult.getErrorSafe()
    page = pageResult?.getError()?.body?.data?.page

    if (
      error.status === HTTPStatus.TemporaryRedirect ||
      error.status === HTTPStatus.PermanentRedirect
    ) {
      return {
        redirect: {
          destination: error.body?.data?.redirect?.url,
          permanent: error.status === HTTPStatus.PermanentRedirect,
        },
      }
    }
    if (error.status === HTTPStatus.InternalServerError) {
      console.error('error', error)
      throw new Error(error.message)
    }
    if (error.status === HTTPStatus.NotFound) {
      ctx.res.statusCode = 404
    }
  } else {
    page = pageResult?.getValueSafe()?.data?.page
  }

  if (!page) {
    return {
      notFound: true,
    }
  }

  page.brand = process.env.BRAND || ''
  page._vsfClient = process.env.BRAND_VSF_CLIENT || ''
  page.backgroundColor = pageResult?.getValueSafe()?.data?.backgroundColor || ''

  const pageData = pageResult?.getValueSafe()?.data
  const dehydratedState =
    pageData?.dehydratedState?.length || session ? dehydrate(queryClient) : {}
  page.accountDrawerConfiguration = pageData?.accountDrawerConfiguration || null

  return {
    props: {
      dehydratedState,
      page,
      key: urlSlug,
      domain,
      gtmId: pageData?.gtmId || null,
      bazaarVoice: pageData?.bazaarVoice || {},
      enabledPaymentMethods: pageData?.enabledPaymentMethods || [],
    },
  }
}

export default Page
